import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/services/api.service';
import { PostCategory, PostItem } from 'src/app/types/post';
import { SearchTermInterface } from 'src/app/types/search';
import { removeTags, upperCaseText } from 'src/app/utils/string-transform';
import { environment } from 'src/environments/environment';
import { SearchSuggestions } from './popular-searches';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
})
export class SearchComponent implements OnInit {
  @ViewChild('searchcontainer') entry: ElementRef;

  xhrInProgress: boolean = false;
  filtersFetched: boolean = false;
  searchContext: string;
  searchObject: SearchTermInterface;
  searchResults: PostItem[];
  popularSearches: string[] = SearchSuggestions.sort(
    () => Math.random() - 0.5
  ).slice(0, 5);
  currentDayName: string;
  categories: PostCategory[];
  specialities: string[];
  currentPage: number = 1;
  amountPerPage: number = 12;
  amountCategories: number = 10;
  hasMoreItems: boolean = true;
  totalResults: number;
  environment = environment;

  constructor(
    public modalController: ModalController,
    private API: ApiService,
    private router: Router
  ) {
    // Get current day name for relevant opening times
    this.currentDayName = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();
  }

  /**
   * Dismiss search modal
   */
  dismissModal() {
    this.modalController.dismiss({
      dismissed: true,
    });
  }

  /**
   * Store open state
   */
  storeOpenClose(item) {
    const element = item.typeTrading[this.currentDayName];
    if (element) {
      return element.open_text + ' - ' + element.close_text;
    }
  }

  /**
   * Link to store detail
   */
  linkToDetail(item) {
    this.router.navigateByUrl(`/shop/${item.slug}`);
    this.dismissModal();
  }

  /**
   * Search results for selected autocomplete suggestion
   */
  selectSuggestion(result) {
    this.currentPage = 1;
    this.amountCategories = 10;

    if (result?.target?.match) {
      if (result.target.type === 'store' && result.target.slug) {
        this.linkToDetail(result.target);
        return;
      }

      this.xhrInProgress = true;
      this.searchObject = result.target;
      this.searchObject.term = removeTags(this.searchObject.match);
      this.searchResults = [];

      let searchEndpoint = `search/${environment.mall_id}/stores?page=${this.currentPage}&amount=${this.amountPerPage}`;
      if (result.target.freeText) {
        searchEndpoint += `&q=${this.searchObject.title}`;
      } else {
        searchEndpoint += `&key=${this.searchObject.type}&val=${this.searchObject.slug}`;
      }

      this.API.GET(searchEndpoint).subscribe({
        next: (response) => {
          this.searchResults = response.results;
          this.xhrInProgress = false;

          if (response.total) this.totalResults = response.total;

          this.hasMoreItems = !(response.results.length < this.amountPerPage);
        },
        error: () => {
          this.xhrInProgress = false;
        },
      });

      this.searchContext =
        this.searchObject.type === 'store'
          ? `Stores`
          : this.searchObject.type === 'occasional'
          ? 'Occasional Products'
          : `${upperCaseText(this.searchObject.type)} - ${
              this.searchObject.parentTitle
                ? upperCaseText(this.searchObject.parentTitle)
                : upperCaseText(this.searchObject.title)
            }`;
    } else {
      this.searchObject = null;
      this.searchResults = [];
    }
  }

  /**
   * Load more search results & increase page number
   */
  loadMore() {
    this.xhrInProgress = true;

    let searchEndpoint = `search/${environment.mall_id}/stores?page=${
      this.currentPage + 1
    }&amount=${this.amountPerPage}`;
    if (this.searchObject) {
      if (this.searchObject?.freeText) {
        searchEndpoint += `&q=${this.searchObject.title}`;
      } else {
        searchEndpoint += `&key=${this.searchObject.type}&val=${
          this.searchObject.slug ?? this.searchObject.term
        }`;
      }
    }

    this.API.GET(searchEndpoint).subscribe({
      next: (response) => {
        if (response.results && response.results[0]) {
          const extendedItems = this.searchResults.concat(response.results);
          this.searchResults = extendedItems;
        }

        if (response.total) this.totalResults = response.total;

        this.currentPage++;
        this.hasMoreItems = !(response.results.length < this.amountPerPage);
        this.xhrInProgress = false;
      },
      error: () => {
        this.xhrInProgress = false;
      },
    });
  }

  /**
   * Fetch search result data
   */
  fetchData(filter = undefined) {
    if (!this.xhrInProgress) {
      this.currentPage = 1;
      this.amountCategories = 10;

      if (this.categories) this.xhrInProgress = true;

      this.API.GET(
        `search/${environment.mall_id}/stores?page=${this.currentPage}&amount=${
          this.amountPerPage
        }${
          filter?.slug
            ? '&key=category&val=' + filter.slug
            : filter
            ? '&key=occasional&val=' + filter
            : ''
        }`
      ).subscribe({
        next: (response) => {
          this.xhrInProgress = false;
          if (!filter) {
            let sortedCategories = [];
            response.categories.forEach((cat) => {
              sortedCategories.push({
                title: cat.title,
                slug: cat.slug,
                count: cat.count,
              });

              if (cat.children && cat.children[0]) {
                cat.children.forEach((child) => {
                  sortedCategories.push({
                    title: child.title,
                    slug: child.slug,
                    count: child.count,
                  });
                });
              }
            });
            this.categories = sortedCategories.sort((a, b) =>
              a.count > b.count ? 1 : -1
            );

            this.specialities = response.occasionals;
            this.filtersFetched = true;
          } else {
            this.searchResults = response.results;

            if (response.total) this.totalResults = response.total;

            this.hasMoreItems = !(response.results.length < this.amountPerPage);
          }
        },
        error: () => {
          this.xhrInProgress = false;
          this.filtersFetched = true;
        },
      });
    }
  }

  /**
   * Fetch data by category
   */
  searchByCategory(category) {
    this.searchObject = {
      ...category,
      originalText: category.title,
      term: category.title,
      type: 'category',
    };
    this.searchContext = 'Categories';
    this.fetchData(category);
  }

  /**
   * Fetch data by occasion
   */
  searchByOccasion(occasion) {
    this.searchObject = {
      originalText: occasion,
      title: occasion,
      term: occasion,
      type: 'occasional',
    };
    this.searchContext = 'Speciality Stores';
    this.fetchData(occasion);
  }

  /**
   * Show more category options
   */
  showMoreCategories() {
    this.amountCategories += 10;
  }

  ngOnInit() {
    this.fetchData();
  }
}
